import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { ProductProgress, ProductStatus } from '@lemoncloud/codes-goods-api';
import { Check } from 'lucide-react';

import { ServiceLoader } from '@codes/assets';
import { Button, Progress, SharedSpinner } from '@codes/ui-kit';

import {
    getDeploymentSteps,
    getProgressValue,
    getStepClassName,
    getStepStatus,
    getStepTextClassName,
    isProgressFailure,
    isProgressSuccess,
} from '../utils/deploymentUtils';

interface ProductCardDeploymentStatusProps {
    progress?: ProductProgress;
    status?: ProductStatus;
    isDeploying?: boolean;
    onRedeploy?: () => void;
}

export const ProductCardDeploymentStatus = ({
    progress,
    status,
    isDeploying,
    onRedeploy,
}: ProductCardDeploymentStatusProps) => {
    const { t } = useTranslation();

    const deploymentSteps = getDeploymentSteps(progress?.states);
    const progressValue = getProgressValue(progress, deploymentSteps.length);
    const progressMessage = progress?.msg || t('product.deployment.inProgress');

    // Failed state - show error message and redeploy button
    if (isProgressFailure(progress)) {
        return (
            <Card className="border border-error/20 bg-error/5 rounded-2xl p-4">
                <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-error flex items-center justify-center shrink-0">
                            <span className="text-white text-xs font-bold">✕</span>
                        </div>
                        <span className="text-sm font-semibold text-error">{t('product.deployment.failed')}</span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={isDeploying}
                            className="ml-auto border-error text-error hover:bg-error hover:text-white"
                            onClick={e => {
                                e.stopPropagation();
                                onRedeploy?.();
                            }}
                        >
                            {isDeploying ? <SharedSpinner /> : t('product.deployment.redeploy')}
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }

    // Success state - show Redeploy button
    if (isProgressSuccess(progress)) {
        return (
            <div className="flex items-center justify-center mb-4">
                <Button
                    className="flex flex-1"
                    disabled={isDeploying}
                    onClick={e => {
                        e.stopPropagation();
                        onRedeploy?.();
                    }}
                >
                    {isDeploying ? <SharedSpinner /> : 'Re-deploy'}
                </Button>
            </div>
        );
    }

    // Don't show if not deploying/busy/failed
    if (!progress && status !== 'busy' && status !== 'idle') {
        return null;
    }

    // In progress state - show for busy/idle status
    return (
        <Card className="border border-gray-300 rounded-2xl py-3 px-[13px] mb-4">
            <div className="flex items-center justify-between">
                <div className="text-base text-gray-900 font-medium">{progressMessage}</div>
                <div className="relative w-12 h-12 flex flex-col items-center justify-center">
                    <Image src={ServiceLoader} className="w-12 h-12 animate-spin" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-medium text-gray-800">
                        {progressValue}%
                    </div>
                </div>
            </div>
            <div className="mt-[13px] mb-4">
                <Progress value={progressValue} />
            </div>
            <ul className="flex items-center justify-between">
                {deploymentSteps.map((step, index) => {
                    const stepStatus = getStepStatus(index, progress, deploymentSteps);
                    const isCompleted = stepStatus === 'completed';

                    return (
                        <li key={step.key} className="flex items-center gap-[5px]">
                            <div className={getStepClassName(stepStatus)}>
                                {isCompleted ? <Check className="text-white w-4 h-4" /> : index + 1}
                            </div>
                            <div className={getStepTextClassName(stepStatus)}>{step.name}</div>
                        </li>
                    );
                })}
            </ul>
        </Card>
    );
};
